import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/base/StructuredData';
import { categories, articles } from '@/mocks/community';

export default function Categories() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredArticles = activeCategory
    ? articles.filter((a) => a.categoryId === activeCategory)
    : articles;

  const webPageSchema = {
    name: '话题分类 - 海角社区',
    description: '浏览海角社区全部话题分类，包括生活分享、情感交流、旅行探索、美食天地、科技数码、读书学习、摄影艺术、运动健康等板块。',
    url: window.location.origin + '/categories',
  };

  return (
    <div className="min-h-screen pt-20">
      <StructuredData type="WebPage" data={webPageSchema} />

      <div className="bg-background-100 py-8 md:py-10">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '话题分类' },
            ]}
          />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">话题分类</h1>
          <p className="text-sm text-foreground-600 mt-2">浏览所有话题板块,发现你感兴趣的内容</p>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 py-3 overflow-x-auto">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                activeCategory === null
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Cards + Article List */}
      <div className="py-10 md:py-14">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {!activeCategory ? (
            /* Category Grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => {
                const catArticles = articles.filter((a) => a.categoryId === cat.slug).length;
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={cat.coverImage}
                        alt={cat.name}
                        title={cat.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900">{cat.name}</h3>
                      </div>
                      <p className="text-xs text-foreground-500 leading-relaxed mb-2 line-clamp-2">{cat.description}</p>
                      <span className="text-xs text-foreground-400">{catArticles} 篇文章</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            /* Filtered Articles */
            <div>
              <div className="flex items-center gap-2 mb-6">
                <button
                  onClick={() => setActiveCategory(null)}
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回全部分类
                </button>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600 font-medium">
                  {categories.find((c) => c.slug === activeCategory)?.name}
                </span>
              </div>

              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-article-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group cursor-pointer bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/85 backdrop-blur-sm text-xs font-medium text-foreground-700">
                          {article.categoryName}
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed mb-3 line-clamp-2">{article.summary}</p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-2">
                            <img src={article.authorAvatar} alt={article.author} className="w-5 h-5 rounded-full object-cover" />
                            <span>{article.author}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line text-xs"></i>
                              {article.readCount >= 1000 ? `${(article.readCount / 1000).toFixed(1)}k` : article.readCount}
                            </span>
                            <span className="flex items-center gap-1">
                              <i className="ri-heart-line text-xs"></i>
                              {article.likeCount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}